import { Injectable, NgZone } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { fromEvent, merge, Subscription, timer } from 'rxjs';
import { environment } from '../../../environments/environment';
import { logindetails } from './login-details';
import { SessionTimeoutDialogComponent } from '../layout/session-timeout-dialog/session-timeout-dialog.component';
import { AuthGuardService } from '../guard/auth-guard/auth-guard.service';

@Injectable({
  providedIn: 'root'
})
export class IdleTimeoutService {
  // Time of inactivity (in ms) before the "Session Timed Out" dialog is shown
  private readonly idleTimeoutMs = 15 * 60 * 1000;
  private readonly activityEvents = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart', 'wheel'];

  private activitySubscription?: Subscription;
  private idleTimerSubscription?: Subscription;
  private dialogOpen = false;

  constructor(private readonly dialog: MatDialog, private readonly ngZone: NgZone, private readonly authSrv: AuthGuardService) { }

  // Starts monitoring user activity. Safe to call multiple times - only the first call takes effect.
  start(): void {
    if (this.activitySubscription) {
      return;
    }
    this.ngZone.runOutsideAngular(() => {
      const activity$ = merge(...this.activityEvents.map(evt => fromEvent(document, evt)));
      this.activitySubscription = activity$.subscribe(() => this.resetTimer());
    });
    this.resetTimer();
  }

  // Stops monitoring user activity (e.g. on logout).
  stop(): void {
    this.activitySubscription?.unsubscribe();
    this.idleTimerSubscription?.unsubscribe();
    this.activitySubscription = undefined;
    this.idleTimerSubscription = undefined;
    this.dialogOpen = false;
  }

  private resetTimer(): void {
    if (this.dialogOpen) {
      return;
    }
    this.idleTimerSubscription?.unsubscribe();
    this.idleTimerSubscription = timer(this.idleTimeoutMs).subscribe(() => {
      this.ngZone.run(() => this.showTimeoutDialog());
    });
  }

  private showTimeoutDialog(): void {
    if (this.dialogOpen) {
      return;
    }
    this.dialogOpen = true;
    this.idleTimerSubscription?.unsubscribe();
    this.activitySubscription?.unsubscribe();

    const dialogRef = this.dialog.open(SessionTimeoutDialogComponent, {
      disableClose: true,
      width: '420px',
      panelClass: 'session-timeout-dialog-panel'
    });

    dialogRef.afterClosed().subscribe(() => {
      this.reLogin();
    });
  }

  // Clears the session and sends the user back to the login page instead of simply reloading the app.
  private reLogin(): void {
    sessionStorage.clear();
    localStorage.clear();
    this.authSrv.clearTokens();
    window.location.href = environment.baseLoginUrl + logindetails.codeChallenge;
  }
}
